import MarkdownIt from 'markdown-it';
import { uniqueHeadingId } from '@/utils/documentWebToc';

// LaTeX 数学公式渲染插件
function latexMathPlugin(md: MarkdownIt) {
  // 行内公式: $...$ 或 \\( ... \\)
  md.inline.ruler.after('escape', 'math_inline', (state, silent) => {
    const start = state.pos;
    const marker = state.src.charCodeAt(start);

    // 检查是否以 $ 开头
    if (marker !== 0x24 /* $ */) return false;

    // 不处理 $$（块级公式）
    if (state.src.charCodeAt(start + 1) === 0x24 /* $$ */) return false;

    const end = state.src.indexOf('$', start + 1);
    if (end === -1 || end === start + 1) return false;

    if (!silent) {
      const token = state.push('math_inline', 'math', 0);
      token.content = state.src.slice(start + 1, end);
      token.markup = '$';
    }

    state.pos = end + 1;
    return true;
  });

  // 块级公式: $$...$$ 或 \\[ ... \\]
  md.block.ruler.before('fence', 'math_block', (state, startLine, endLine, silent) => {
    const start = state.bMarks[startLine] + state.tShift[startLine];
    const max = state.eMarks[startLine];

    if (start + 2 > max) return false;
    if (state.src.charCodeAt(start) !== 0x24 /* $ */) return false;
    if (state.src.charCodeAt(start + 1) !== 0x24 /* $ */) return false;

    let pos = start + 2;
    while (pos < max && state.src.charCodeAt(pos) === 0x24) pos++;

    const markerCount = pos - start;
    if (markerCount !== 2) return false;

    let nextLine = startLine + 1;
    let endStart = -1;

    for (; nextLine < endLine; nextLine++) {
      const lineStart = state.bMarks[nextLine] + state.tShift[nextLine];
      const lineMax = state.eMarks[nextLine];

      for (let i = lineStart; i < lineMax; i++) {
        if (state.src.charCodeAt(i) === 0x24) {
          let j = i + 1;
          while (j < lineMax && state.src.charCodeAt(j) === 0x24) j++;

          if (j - i === 2) {
            endStart = i;
            break;
          }
        }
      }
      if (endStart !== -1) break;
    }

    if (endStart === -1) return false;

    if (!silent) {
      const token = state.push('math_block', 'math', 0);
      token.block = true;
      token.content = state.src.slice(pos, endStart).trim();
      token.markup = '$$';
      token.map = [startLine, nextLine + 1];
      state.line = nextLine + 1;
    }

    return true;
  });

  // 渲染规则 - 使用 HTML 和 Unicode 表示数学公式
  md.renderer.rules.math_inline = (tokens, idx) => {
    const content = tokens[idx].content;
    // 返回纯文本，避免展示 HTML（例如 math-text / math-inline 包裹）
    return renderSimpleMath(content);
  };

  md.renderer.rules.math_block = (tokens, idx) => {
    const content = tokens[idx].content;
    // 返回纯文本，避免展示 math-block 盒子
    return renderSimpleMath(content);
  };
}

// 简单的数学符号渲染（将 LaTeX 符号转换为可读格式）
function renderSimpleMath(latex: string): string {
  let result = latex;

  // 处理 \text{} - 用于单位等的正体文本
  // 需要在其他替换之前处理，因为内容不应该被斜体化
  // 不生成 span，直接输出纯文本单位，避免 HTML 残留
  result = result.replace(/\\text\{([^}]+)\}/g, '$1');

  // 常见希腊字母
  const greekLetters: Record<string, string> = {
    '\\alpha': 'α', '\\beta': 'β', '\\gamma': 'γ', '\\delta': 'δ', '\\epsilon': 'ε',
    '\\zeta': 'ζ', '\\eta': 'η', '\\theta': 'θ', '\\iota': 'ι', '\\kappa': 'κ',
    '\\lambda': 'λ', '\\mu': 'μ', '\\nu': 'ν', '\\xi': 'ξ', '\\pi': 'π',
    '\\rho': 'ρ', '\\sigma': 'σ', '\\tau': 'τ', '\\upsilon': 'υ', '\\phi': 'φ',
    '\\chi': 'χ', '\\psi': 'ψ', '\\omega': 'ω', '\\Gamma': 'Γ', '\\Delta': 'Δ',
    '\\Theta': 'Θ', '\\Lambda': 'Λ', '\\Xi': 'Ξ', '\\Pi': 'Π', '\\Sigma': 'Σ',
    '\\Phi': 'Φ', '\\Psi': 'Ψ', '\\Omega': 'Ω',
  };

  // 常见数学符号
  const mathSymbols: Record<string, string> = {
    '\\times': '×', '\\div': '÷', '\\cdot': '·', '\\pm': '±', '\\mp': '∓',
    '\\leq': '≤', '\\geq': '≥', '\\neq': '≠', '\\approx': '≈', '\\equiv': '≡',
    '\\infty': '∞', '\\partial': '∂', '\\nabla': '∇', '\\int': '∫', '\\sum': 'Σ',
    '\\prod': '∏', '\\sqrt': '√', '\\to': '→', '\\rightarrow': '→', '\\leftarrow': '←',
    '\\leftrightarrow': '↔', '\\circ': '○', '\\bullet': '•', '\\star': '★', '\\ast': '*',
    '\\propto': '∝', '\\sim': '∼', '\\simeq': '≃', '\\cong': '≅', '\\subset': '⊂',
    '\\supset': '⊃', '\\subseteq': '⊆', '\\supseteq': '⊇', '\\in': '∈', '\\notin': '∉',
    '\\cup': '∪', '\\cap': '∩', '\\emptyset': '∅', '\\forall': '∀', '\\exists': '∃',
    '\\nexists': '∄', '\\neg': '¬', '\\wedge': '∧', '\\vee': '∨', '\\Leftrightarrow': '⇔',
    '\\angle': '∠', '\\perp': '⊥',
    '\\parallel': '∥', '\\degree': '°', '\\prime': '′', '\\hbar': 'ℏ', '\\ell': 'ℓ',
  };

  // 上标和下标
  // 不生成 sup/sub HTML。用 ^ / _ 纯文本表达，保证可读性且无 HTML 残留。
  result = result.replace(/\^\{([^}]+)\}/g, '^{$1}');
  result = result.replace(/\^([a-zA-Z0-9])/g, '^$1');
  result = result.replace(/_\{([^}]+)\}/g, '_{$1}');
  result = result.replace(/_([a-zA-Z0-9])/g, '_$1');

  // 分数
  result = result.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1/$2)');

  // 括号
  result = result.replace(/\\left\(/g, '(');
  result = result.replace(/\\right\)/g, ')');
  result = result.replace(/\\left\[/g, '[');
  result = result.replace(/\\right\]/g, ']');
  result = result.replace(/\\left\{/g, '{');
  result = result.replace(/\\right\}/g, '}');
  result = result.replace(/\\{/g, '{');
  result = result.replace(/\\}/g, '}');

  // 替换希腊字母和数学符号
  Object.entries(greekLetters).forEach(([latex, unicode]) => {
    result = result.replace(new RegExp(latex.replace(/\\/g, '\\\\'), 'g'), unicode);
  });

  Object.entries(mathSymbols).forEach(([latex, unicode]) => {
    result = result.replace(new RegExp(latex.replace(/\\/g, '\\\\'), 'g'), unicode);
  });

  // 处理 \, 命令（LaTeX中的空格）
  result = result.replace(/\\\s+/g, ' ');

  // 清理多余的反斜杠（但要保留 \text 已经处理过的）
  result = result.replace(/\\([a-zA-Z]+)/g, '$1');

  // 再次清理任何残留的反斜杠
  result = result.replace(/\\/g, '');

  return escapeHtml(result);
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
});

function headingTextFromTokens(
  tokens: Array<{ type?: string; content?: string; children?: Array<{ content?: string }> | null }>,
  idx: number,
): string {
  const inline = tokens[idx + 1];
  if (inline?.type === 'inline') {
    if (inline.content) return inline.content;
    return (inline.children || []).map((c) => c.content || '').join('');
  }
  return '';
}

function headingAnchorPlugin(instance: MarkdownIt) {
  const used = new Set<string>();
  instance.core.ruler.before('block', 'reset_heading_ids', () => {
    used.clear();
  });
  const defaultOpen = instance.renderer.rules.heading_open
    || ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
  instance.renderer.rules.heading_open = (tokens, idx, options, env, self) => {
    const title = headingTextFromTokens(tokens, idx);
    if (title) {
      tokens[idx].attrSet('id', uniqueHeadingId(title, used));
    }
    return defaultOpen(tokens, idx, options, env, self);
  };
}

// 使用 LaTeX 数学公式插件
latexMathPlugin(md);
headingAnchorPlugin(md);

// 过滤AI问候语的辅助函数
// 按优先级排序，先匹配完整的问候语，再匹配简短的
const GREETING_PATTERNS = [
  // 完整的问候语："你好！我是你的力学计算助手。" 或 "你好，我是你的XX计算助手。"
  /^你好[！!，。]?\s*我是你的[^，。]+助手[，。]?\s*/i,
  /^您好[！!，。]?\s*我是你的[^，。]+助手[，。]?\s*/i,
  // 稍微通用的模式
  /^你好[！!，。]?\s*我是(?:你的)?.+?[，。]\s*/i,
  /^您好[！!，。]?\s*我是(?:你的)?.+?[，。]\s*/i,
  // 简短的问候语
  /^你好[！!。，]?\s*/i,
  /^您好[！!。，]?\s*/i,
  // 其他常见开场白
  /^我是(?:你的)?[^，。]+?[，。]\s*/i,
  /^(?:很高兴|非常荣幸)(?:为你|为您)[^，。]+?[，。]\s*/i,
];

export function removeAIGreetings(text: string): string {
  let result = text;
  for (const pattern of GREETING_PATTERNS) {
    result = result.replace(pattern, '');
  }
  return result.trim();
}

export function renderMarkdown(raw: string): string {
  if (!raw) return '';
  const text = raw.replace(/\s*\[DONE\]\s*$/i, '');
  return md.render(text);
}

// 专门用于计算器结果渲染的函数
export function renderCalculatorResult(raw: string): string {
  if (!raw) return '';
  const plainText = raw
    .replace(/\s*\[DONE\]\s*$/gi, '')
    .replace(/&(lt|gt|amp|quot|#39);/g, (m) => {
      const entityMap: Record<string, string> = {
        '&lt;': '<',
        '&gt;': '>',
        '&amp;': '&',
        '&quot;': '"',
        '&#39;': "'",
      };
      return entityMap[m] || m;
    })
    .replace(/<span[^>]*class=["']math-text["'][^>]*>(.*?)<\/span>/gi, '$1')
    .replace(/<sup>\s*2\s*<\/sup>/gi, '²')
    .replace(/<sup>\s*3\s*<\/sup>/gi, '³')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?[^>]+>/g, '');
  // 移除问候语
  const cleaned = removeAIGreetings(plainText);
  // 渲染markdown（包含LaTeX数学公式）
  return md.render(cleaned);
}
